import type { StaffPublicConfig } from './api/backend'

export interface WidgetConfig {
  staffId: string
  mode: 'floating' | 'inline'
  container: string | null
  position: 'bottom-center' | 'bottom-left' | 'bottom-right'
  trigger: 'scroll' | 'delay' | 'immediate'
  scrollThreshold: number
  delayMs: number
  theme: 'light' | 'dark' | 'auto'
  accentColor: string | null
  language: string
  backendUrl: string
  visionWsUrl: string | null
  visionConsent: 'auto' | 'required'
  /** When true, route decisions through the v1 Bowtie orchestrator instead of the legacy vision loop. */
  v1Mode: boolean
  /** Override for the v1 WS endpoint. Defaults to the production Cloud Run URL. */
  v1WsUrl: string | null
  /** Optional tenant identifier for per-tenant catalog enable/disable overrides. */
  tenantId: string | null
}

const DEFAULTS: Omit<WidgetConfig, 'staffId'> = {
  mode: 'floating',
  container: null,
  position: 'bottom-center',
  trigger: 'scroll',
  scrollThreshold: 0.3,
  delayMs: 3000,
  theme: 'light',
  accentColor: null,
  language: 'en',
  backendUrl: 'https://us-central1-holostaff-ai.cloudfunctions.net',
  visionWsUrl: null,
  visionConsent: 'auto',
  v1Mode: false,
  v1WsUrl: null,
  tenantId: null,
}

/**
 * Extract widget configuration from the hosting script tag's data-* attributes.
 * Embed attributes take priority; DEFAULTS are used as fallback.
 */
export function extractConfig(scriptEl: HTMLScriptElement): WidgetConfig {
  const staffId = scriptEl.dataset.staffId
  if (!staffId) {
    throw new Error('[HolostaffWidget] data-staff-id attribute is required')
  }

  return {
    staffId,
    mode: (scriptEl.dataset.mode as WidgetConfig['mode']) ?? DEFAULTS.mode,
    container: scriptEl.dataset.container ?? DEFAULTS.container,
    position: (scriptEl.dataset.position as WidgetConfig['position']) ?? DEFAULTS.position,
    trigger: (scriptEl.dataset.trigger as WidgetConfig['trigger']) ?? DEFAULTS.trigger,
    scrollThreshold: parseFloat(scriptEl.dataset.scrollThreshold ?? '') || DEFAULTS.scrollThreshold,
    delayMs: parseInt(scriptEl.dataset.delayMs ?? '', 10) || DEFAULTS.delayMs,
    theme: (scriptEl.dataset.theme as WidgetConfig['theme']) ?? DEFAULTS.theme,
    accentColor: scriptEl.dataset.accentColor ?? DEFAULTS.accentColor,
    language: scriptEl.dataset.language ?? DEFAULTS.language,
    backendUrl: scriptEl.dataset.backendUrl ?? DEFAULTS.backendUrl,
    visionWsUrl: scriptEl.dataset.visionWsUrl ?? DEFAULTS.visionWsUrl,
    visionConsent: (scriptEl.dataset.hsConsent as WidgetConfig['visionConsent']) ?? DEFAULTS.visionConsent,
    v1Mode: scriptEl.dataset.v1Mode === 'true',
    v1WsUrl: scriptEl.dataset.v1WsUrl ?? DEFAULTS.v1WsUrl,
    tenantId: scriptEl.dataset.tenantId ?? DEFAULTS.tenantId,
  }
}

const VALID_POSITIONS = ['bottom-center', 'bottom-left', 'bottom-right']
const VALID_TRIGGERS = ['scroll', 'delay', 'immediate']
const VALID_THEMES = ['light', 'dark', 'auto']

/**
 * Apply server-side widget settings as defaults for any fields not explicitly
 * set via embed data-* attributes. Embed attrs always win.
 */
export function applyServerDefaults(
  config: WidgetConfig,
  scriptEl: HTMLScriptElement,
  serverConfig: StaffPublicConfig,
): WidgetConfig {
  const updated = { ...config }

  if (!scriptEl.dataset.trigger && serverConfig.trigger && VALID_TRIGGERS.includes(serverConfig.trigger)) {
    updated.trigger = serverConfig.trigger as WidgetConfig['trigger']
  }
  if (!scriptEl.dataset.position && serverConfig.position && VALID_POSITIONS.includes(serverConfig.position)) {
    updated.position = serverConfig.position as WidgetConfig['position']
  }
  if (!scriptEl.dataset.theme && serverConfig.theme && VALID_THEMES.includes(serverConfig.theme)) {
    updated.theme = serverConfig.theme as WidgetConfig['theme']
  }
  if (!scriptEl.dataset.scrollThreshold && serverConfig.scrollThreshold != null) {
    updated.scrollThreshold = serverConfig.scrollThreshold
  }
  if (!scriptEl.dataset.delayMs && serverConfig.delayMs != null) {
    updated.delayMs = serverConfig.delayMs
  }
  if (!scriptEl.dataset.visionWsUrl && serverConfig.visionWsUrl) {
    updated.visionWsUrl = serverConfig.visionWsUrl
  }

  return updated
}
